import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg, fmtTime, SLOTS } from "@/lib/api";
import { PageHead, card, Field, Sel, inputCls, RecordBadge, useCatalog, HospitalSelect, Empty } from "@/components/admin/kit";
import { MedicineForm, M0 } from "@/components/admin/MedicineForm";
import { MedList } from "./Medicines";
import { useCapGuard } from "@/components/admin/CapGuard";
import { Appointments } from "@/components/admin/Appointments";
import { UpcomingCalls } from "@/components/admin/CallPreview";

const P0 = { name: "", name_voice: "", age: "", gender: "", language: "te-IN", pace: 1.0, call_receiver: "self", call_times: { morning: "08:00", afternoon: "13:30", night: "21:00" }, record_type: "demo", calls_stopped: false, doctor_set_times: [], relationship: "", relationship_other: "", voice_override: null };

function FamilyFields({ f, setF }) {
  const [opts] = useLoad("/call-types/options");
  const [langs] = useLoad("/languages");
  const te = (langs || []).find((l) => l.code === (f.language || "te-IN")) || {};
  const voices = [...new Set([...Object.values(te.slot_voices || {}), ...(te.voice_options || [])])];
  const child = f.age !== "" && Number(f.age) < 18;
  return (
    <>
      {child && <p className="rounded-xl bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-800 sm:col-span-2" data-testid="patient-child-note">Under 18: calls always go to the parent. Choose “Family member” and add the parent's name below.</p>}
      {f.call_receiver === "caregiver" && !child && (
        <Field label="Relationship to the family member" hint="Decides ఆయనకి / ఆవిడకి in the call"><Sel value={f.relationship} onChange={(v) => setF({ ...f, relationship: v })} placeholder="Choose" options={(opts?.relations || []).map(([k, w]) => [k, k === "other" ? "Other" : `${w} (${k.replace("_m", " · mother's side").replace("_p", " · father's side")})`])} testid="patient-relationship-select" /></Field>
      )}
      {f.call_receiver === "caregiver" && !child && f.relationship === "other" && (
        <Field label="Relationship word (Telugu)" hint="He/she is taken from the patient's gender"><input className={`${inputCls} font-te`} value={f.relationship_other} onChange={(e) => setF({ ...f, relationship_other: e.target.value })} data-testid="patient-relationship-other-input" /></Field>
      )}
      <Field label="Voice for this patient" hint="Default: Morning / Afternoon / Night voices"><Sel value={f.voice_override || ""} onChange={(v) => setF({ ...f, voice_override: v || null })} placeholder="Voice by time slot (default)" options={voices.map((v) => [v, `Use one voice for all calls: ${v.split("_")[0].replace(/^./, (c) => c.toUpperCase())}`])} testid="patient-voice-override-select" /></Field>
    </>
  );
}

function PatientFields({ f, setF }) {
  const s = (k) => (v) => setF({ ...f, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Patient name *"><input className={inputCls} required value={f.name} onChange={s("name")} data-testid="patient-name-input" /></Field>
      <Field label="Name as spoken" hint="e.g. లక్ష్మి"><input className={`${inputCls} font-te`} value={f.name_voice} onChange={s("name_voice")} data-testid="patient-voice-input" /></Field>
      <Field label="Age *"><input type="number" className={inputCls} required value={f.age} onChange={s("age")} data-testid="patient-age-input" /></Field>
      <Field label="Gender"><Sel value={f.gender} onChange={s("gender")} placeholder="—" options={[["female", "Female"], ["male", "Male"], ["other", "Other"]]} testid="patient-gender-select" /></Field>
      <Field label="Language"><input className={inputCls} value={f.language} onChange={s("language")} data-testid="patient-language-input" /></Field>
      <Field label="Voice pace"><Sel value={String(f.pace)} onChange={(v) => setF({ ...f, pace: Number(v) })} options={[["0.85", "0.85 · elderly / slow"], ["1", "1.0 · normal"]]} testid="patient-pace-select" /></Field>
      <Field label="Who receives the calls"><Sel value={f.call_receiver} onChange={s("call_receiver")} options={[["self", "Patient"], ["caregiver", "Family member"]]} testid="patient-receiver-select" /></Field>
      <Field label="Record type"><Sel value={f.record_type} onChange={s("record_type")} options={[["demo", "Demo"], ["real_test", "REAL – TEST ONLY (family test)"], ["sample", "Sample (website)"]]} testid="patient-record-select" /></Field>
      <div className="grid grid-cols-3 gap-2 sm:col-span-2">
        {SLOTS.map((sl) => (
          <Field key={sl} label={`${sl} call`}>
            <input type="time" className={inputCls} value={f.call_times[sl]} onChange={(e) => setF({ ...f, call_times: { ...f.call_times, [sl]: e.target.value } })} data-testid={`patient-time-${sl}`} />
            <label className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground"><input type="checkbox" checked={(f.doctor_set_times || []).includes(sl)} onChange={(e) => setF({ ...f, doctor_set_times: e.target.checked ? [...(f.doctor_set_times || []), sl] : (f.doctor_set_times || []).filter((x) => x !== sl) })} data-testid={`patient-doctor-set-${sl}`} /> Doctor set this time</label>
          </Field>
        ))}
      </div>
      <p className="text-xs text-muted-foreground sm:col-span-2">Quiet hours: no calls 9:30 PM – 7:00 AM (change in Settings) unless the doctor set that dose time.</p>
      <FamilyFields f={f} setF={setF} />
    </div>
  );
}

function PhoneCheck({ phone, setPhone, onSame }) {
  const api = useApi();
  const [hit, setHit] = useState(null);
  const check = async () => {
    if (phone.replace(/\D/g, "").length < 10) return setHit(null);
    setHit((await api.get(`/contacts/lookup`, { params: { phone } })).data);
  };
  return (
    <div className="grid gap-2">
      <Field label="Patient / caregiver phone *" hint="We check if this number already belongs to someone"><input className={inputCls} required value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={check} data-testid="patient-phone-input" /></Field>
      {hit?.matches?.length > 0 && (
        <div className="rounded-2xl border border-orange-300 bg-orange-50 p-3 text-sm" data-testid="phone-duplicate-box">
          <p className="font-semibold">This phone already belongs to:</p>
          {hit.matches.map((m) => (
            <div key={m.patient_id} className="mt-2 flex flex-wrap items-center gap-2">
              <span data-testid="phone-duplicate-label">{m.label}</span><span className="text-xs text-muted-foreground">({m.role})</span>
              <span className="font-semibold">Same person?</span>
              <Button type="button" size="sm" onClick={() => onSame(m.patient_id)} data-testid="phone-same-yes">Yes, open record</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setHit(null)} data-testid="phone-same-no">No, different person</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Patients() {
  const api = useApi();
  const nav = useNavigate();
  const { patients, reload } = useCatalog();
  const [f, setF] = useState(null);
  const [phone, setPhone] = useState("");
  const [cg, setCg] = useState({ name: "", name_voice: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      const p = (await api.post("/patients", { ...f, age: Number(f.age) })).data;
      await api.post(`/patients/${p.id}/contacts`, f.call_receiver === "caregiver" ? { phone, ...cg, role: "caregiver" } : { phone, name: f.name, name_voice: f.name_voice, role: "self" });
      reload();
      toast.success("Patient added");
      nav(`/admin/patients/${p.id}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="patients-page">
      <PageHead title="Patients" sub="The phone number is not the patient ID — one phone can belong to several patients.">
        <Button onClick={() => { setF(P0); setPhone(""); }} data-testid="add-patient-btn"><Plus size={16} /> Add patient</Button>
      </PageHead>
      <div className={`${card} overflow-x-auto p-0 sm:p-0`}>
        <table className="w-full text-sm">
          <thead className="bg-[var(--lav)] text-left text-xs uppercase text-muted-foreground"><tr><th className="p-3">Name</th><th className="p-3">Age</th><th className="p-3">Calls to</th><th className="p-3">Pace</th><th className="p-3">Times</th><th className="p-3" /></tr></thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.id} className="border-t border-[var(--lav-2)] hover:bg-[var(--lav)]/60" data-testid={`patient-row-${p.id}`}>
                <td className="p-3"><Link to={`/admin/patients/${p.id}`} className="font-semibold text-[var(--violet)] hover:underline">{p.name}</Link> <span className="font-te text-muted-foreground">{p.name_voice}</span> <RecordBadge type={p.record_type} />{p.calls_stopped && <span className="ml-1 rounded-full bg-purple-100 px-2 text-[10px] font-bold text-purple-800">STOPPED (9)</span>}</td>
                <td className="p-3">{p.age}</td><td className="p-3">{p.call_receiver === "caregiver" ? "Family member" : "Patient"}</td><td className="p-3">{p.pace}</td>
                <td className="p-3 text-xs">{SLOTS.map((s) => p.call_times?.[s]).join(" · ")}</td>
                <td className="p-3 text-right"><Link to={`/admin/patients/${p.id}`} className="text-xs font-semibold text-[var(--violet)]">Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"><DialogHeader><DialogTitle>Add patient</DialogTitle></DialogHeader>
          {f && (
            <form className="grid gap-4" onSubmit={create} data-testid="patient-form">
              <PatientFields f={f} setF={setF} />
              {f.call_receiver === "caregiver" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Family member name *"><input className={inputCls} required value={cg.name} onChange={(e) => setCg({ ...cg, name: e.target.value })} data-testid="caregiver-name-input" /></Field>
                  <Field label="Family member name as spoken"><input className={`${inputCls} font-te`} value={cg.name_voice} onChange={(e) => setCg({ ...cg, name_voice: e.target.value })} data-testid="caregiver-voice-input" /></Field>
                </div>
              )}
              <PhoneCheck phone={phone} setPhone={setPhone} onSame={(id) => { setF(null); nav(`/admin/patients/${id}`); }} />
              <p className="text-xs text-muted-foreground">Add hospital consent (and a family contact, if any) on the next screen.</p>
              <Button data-testid="patient-save-btn">Create patient</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Contacts({ pid }) {
  const api = useApi();
  const [list, reload] = useLoad(`/patients/${pid}/contacts`);
  const [c, setC] = useState({ phone: "", name: "", name_voice: "", role: "family_alert" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/patients/${pid}/contacts`, c); setC({ ...c, phone: "", name: "", name_voice: "" }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className={card} data-testid="contacts-panel">
      <p className="mb-3 font-bold">Contacts</p>
      {(list || []).map((x) => (
        <div key={x.id + x.role} className="flex flex-wrap items-center gap-2 border-b border-[var(--lav-2)] py-2 text-sm">
          <span className="rounded-full bg-[var(--lav-2)] px-2 text-xs font-semibold">{x.role.replace("_", " ")}</span><b>{x.name}</b><span className="font-te text-muted-foreground">{x.name_voice}</span><span className="text-muted-foreground">{x.phone}</span>
          <button className="ml-auto text-muted-foreground" onClick={async () => { await api.delete(`/patients/${pid}/contacts/${x.id}`, { params: { role: x.role } }); reload(); }} data-testid={`remove-contact-${x.id}-${x.role}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <form onSubmit={add} className="mt-3 grid gap-2 sm:grid-cols-5" data-testid="contact-form">
        <Sel value={c.role} onChange={(v) => setC({ ...c, role: v })} options={[["family_alert", "Family contact (alerts off in pilot)"], ["caregiver", "Caregiver"], ["self", "Patient (self)"]]} testid="contact-role-select" />
        <input className={inputCls} placeholder="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="contact-name-input" />
        <input className={`${inputCls} font-te`} placeholder="Name as spoken" value={c.name_voice} onChange={(e) => setC({ ...c, name_voice: e.target.value })} data-testid="contact-voice-input" />
        <input className={inputCls} required placeholder="Phone" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} data-testid="contact-phone-input" />
        <Button data-testid="contact-add-btn">Add contact</Button>
      </form>
    </div>
  );
}

function Enrollments({ pid }) {
  const api = useApi();
  const { hospitals } = useCatalog();
  const [list, reload] = useLoad(`/enrollments?patient_id=${pid}`);
  const [hid, setHid] = useState("");
  const save = async (e, patch) => {
    try { await api.put(`/enrollments/${e.id}`, { ...e, ...patch }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const add = async () => {
    if (!hid) return;
    try { await api.post("/enrollments", { patient_id: pid, hospital_id: hid, consent: false, paying: true, health_updates_opt_in: false }); setHid(""); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className={card} data-testid="enrollments-panel">
      <p className="mb-1 font-bold">Hospitals (enrollments)</p>
      <p className="mb-3 text-xs text-muted-foreground">Consent is required before any real call. It is logged with date and time.</p>
      {(list || []).map((e) => (
        <div key={e.id} className="grid gap-2 border-b border-[var(--lav-2)] py-3 text-sm sm:grid-cols-[1fr_auto_auto_auto_auto] sm:items-center" data-testid={`enrollment-${e.id}`}>
          <b>{hospitals.find((h) => h.id === e.hospital_id)?.name || "—"}</b>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={e.consent} onChange={(x) => save(e, { consent: x.target.checked })} data-testid={`enrollment-consent-${e.id}`} /> Consent {e.consent_at && <span className="text-[11px] text-muted-foreground">({fmtTime(e.consent_at)})</span>}</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={e.paying} onChange={(x) => save(e, { paying: x.target.checked })} data-testid={`enrollment-paying-${e.id}`} /> Paying</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={e.health_updates_opt_in} onChange={(x) => save(e, { health_updates_opt_in: x.target.checked })} data-testid={`enrollment-optin-${e.id}`} /> Health updates</label>
          <button className="text-muted-foreground" onClick={async () => { await api.delete(`/enrollments/${e.id}`); reload(); }} data-testid={`enrollment-delete-${e.id}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="mt-3 flex gap-2"><HospitalSelect value={hid} onChange={setHid} testid="enroll-hospital-select" /><Button onClick={add} data-testid="enroll-add-btn">Link</Button></div>
    </div>
  );
}

export function PatientDetail() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const { reload: reloadCat } = useCatalog();
  const [p, reload] = useLoad(`/patients/${id}`);
  const [edit, setEdit] = useState(null);
  const [med, setMed] = useState(null);
  const [mk, setMk] = useState(0);
  const { guard, dialog } = useCapGuard();
  if (!p) return <Empty>Loading…</Empty>;
  const saveP = async (e) => {
    e.preventDefault();
    try { await api.put(`/patients/${id}`, { ...edit, age: Number(edit.age) }); setEdit(null); reload(); reloadCat(); toast.success("Saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  const saveMed = async (body) => {
    if (!(await guard(id, [{ ...body, id: med.id }]))) return;
    try { med.id ? await api.put(`/medicines/${med.id}`, body) : await api.post("/medicines", body); setMed(null); setMk((k) => k + 1); reload(); toast.success("Medicine saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="patient-detail-page">
      {dialog}
      <PageHead title={<span className="flex flex-wrap items-center gap-2">{p.name} <span className="font-te text-xl text-muted-foreground">{p.name_voice}</span><RecordBadge type={p.record_type} /></span>}
        sub={`${p.age} yrs · ${p.language} · pace ${p.pace} · calls to ${p.call_receiver} · ${SLOTS.map((s) => p.call_times?.[s]).join(" / ")}`}>
        {p.calls_stopped && <Button variant="outline" onClick={async () => { await api.put(`/patients/${id}`, { ...p, calls_stopped: false }); reload(); }} data-testid="resume-calls-btn">Calls stopped (9) · Resume</Button>}
        <Button variant="outline" onClick={() => setEdit(p)} data-testid="edit-patient-btn"><Pencil size={15} /> Edit</Button>
        <Button variant="ghost" onClick={async () => { if (window.confirm("Delete this patient and all their medicines?")) { await api.delete(`/patients/${id}`); reloadCat(); nav("/admin/patients"); } }} data-testid="delete-patient-btn"><Trash2 size={15} /></Button>
      </PageHead>
      <div className="grid gap-4 xl:grid-cols-2"><Enrollments pid={id} /><Contacts pid={id} /></div>
      <div className={`${card} mt-4`}>
        <div className="mb-3 flex items-center"><p className="font-bold">Medicines</p><Button size="sm" className="ml-auto" onClick={() => setMed(M0(id))} data-testid="add-medicine-btn"><Plus size={14} /> Add medicine</Button></div>
        <MedList key={mk} patientId={id} onEdit={setMed} />
      </div>
      <Appointments pid={id} />
      <div className="mt-4"><UpcomingCalls pid={id} /></div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"><DialogHeader><DialogTitle>Edit patient</DialogTitle></DialogHeader>
          {edit && <form onSubmit={saveP} className="grid gap-4"><PatientFields f={edit} setF={setEdit} /><Button data-testid="patient-update-btn">Save</Button></form>}
        </DialogContent>
      </Dialog>
      <Dialog open={!!med} onOpenChange={(o) => !o && setMed(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl"><DialogHeader><DialogTitle>{med?.id ? "Edit medicine" : "Add medicine"}</DialogTitle></DialogHeader>
          {med && <MedicineForm init={{ ...med, hospital_id: med.hospital_id || "", doctor_id: med.doctor_id || "" }} onSave={saveMed} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
